/**
 * MontaTech v2 - Native C# Shape Collector
 *
 * Coleta shapes via C# nativo (action: nesting:collect-native)
 * Alternativa mais rápida à coleta VBA para benchmark A/B.
 *
 * FLUXO:
 * 1. Envia action "nesting:collect-native" ao C# via WebView2
 * 2. C# coleta shapes diretamente via COM (sem VBA intermediário)
 * 3. Retorna Render2DObject[] normalizados
 * 4. Converte para MontaTechCurveData (compatível com fluxo existente)
 */

import { corelCommunication } from '../../../../lib/corel/communication';
import { sendToTerminal } from '../../../../lib/terminalLogger';
import type { MontaTechCurveData } from '../types';

type NativeCurve = MontaTechCurveData['curves'][number];

interface NativeCollectStats {
  shapeCount: number;
  pathCount: number;
  segmentCount: number;
  durationMs: number;
}

interface NativeCollectResult {
  success: boolean;
  data?: MontaTechCurveData;
  stats?: NativeCollectStats;
  error?: string;
}

/**
 * Coleta shapes via C# nativo (nesting:collect-native)
 * Retorna dados compatíveis com MontaTechCurveData para uso no canvas existente.
 */
export function collectShapesNative(): Promise<NativeCollectResult> {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const timeoutMs = 15000;

    sendToTerminal('⚡ [NATIVE-COLLECT] Enviando action nesting:collect-native ao C#');

    const requestId = corelCommunication.sendActionGuarded(
      'nesting:collect-native',
      {},
      (response: any) => {
        const elapsed = Date.now() - startTime;

        sendToTerminal('📥 [NATIVE-COLLECT] Resposta recebida do C#', {
          status: response?.status,
          hasResult: !!response?.result,
          elapsedMs: elapsed
        });

        if (response?.status === 'success' && response?.result) {
          const result = response.result;
          const objects = result.objects || [];
          const stats: NativeCollectStats = {
            shapeCount: result.stats?.shapeCount ?? objects.length,
            pathCount: result.stats?.pathCount ?? 0,
            segmentCount: result.stats?.segmentCount ?? 0,
            durationMs: result.stats?.durationMs ?? elapsed
          };

          // Converter Render2DObject[] para MontaTechCurveData
          const curveData = convertToCurveData(objects, stats, elapsed);

          sendToTerminal('✅ [NATIVE-COLLECT] Conversão concluída', {
            objectCount: objects.length,
            curveCount: curveData.curves.length,
            stats
          });

          resolve({ success: true, data: curveData, stats });
        } else {
          const errorMsg = response?.message || response?.error || 'Erro na coleta nativa';
          sendToTerminal('❌ [NATIVE-COLLECT] Falha', { error: errorMsg, code: response?.code });
          resolve({ success: false, error: errorMsg });
        }
      },
      timeoutMs
    );

    sendToTerminal('🧾 [NATIVE-COLLECT] Resultado do envio', {
      requestId: requestId || null,
      timeoutMs,
      webViewAvailable: corelCommunication.isWebViewAvailable(),
      pendingRequests: corelCommunication.getPendingRequestsCount()
    });

    if (!requestId) {
      sendToTerminal('❌ [NATIVE-COLLECT] Falha ao enviar action (WebView indisponível)');
      resolve({ success: false, error: 'WebView2 não disponível para coleta nativa' });
    }
  });
}

/**
 * Converte Render2DObject[] (formato C#) para MontaTechCurveData (formato frontend)
 *
 * Formato C# real (camelCase do JSON serializer):
 * { id, width, height, maxLength, rotationDeg,
 *   paths: [{ closed, segments: [{ snp: {x,y}, scp: {x,y}, ecp: {x,y} }] }] }
 */
function convertToCurveData(
  objects: any[],
  stats: NativeCollectStats,
  elapsedMs: number
): MontaTechCurveData {
  const curves = objects.map((obj: any, i: number) => {
    const rawPaths = obj.paths || obj.Paths || [];
    const paths = rawPaths.map((p: any) => {
      const rawSegments = p.segments || p.Segments || [];
      return {
        segmentCount: rawSegments.length,
        closed: p.closed ?? p.Closed ?? true,
        segments: rawSegments.map((s: any) => ({
          // C# retorna snp: {x, y} (objetos aninhados)
          SNPx: s.snp?.x ?? s.SNPx ?? s.snpx ?? 0,
          SNPy: s.snp?.y ?? s.SNPy ?? s.snpy ?? 0,
          SCPx: s.scp?.x ?? s.SCPx ?? s.scpx ?? 0,
          SCPy: s.scp?.y ?? s.SCPy ?? s.scpy ?? 0,
          ECPx: s.ecp?.x ?? s.ECPx ?? s.ecpx ?? 0,
          ECPy: s.ecp?.y ?? s.ECPy ?? s.ecpy ?? 0
        }))
      };
    });

    // C# usa "id" (não "index")
    const index = obj.id ?? obj.Id ?? obj.index ?? obj.Index ?? (i + 1);

    return {
      index,
      width: obj.width ?? obj.Width ?? 0,
      height: obj.height ?? obj.Height ?? 0,
      maxLength: obj.maxLength ?? obj.MaxLength ?? 0,
      // C# Render2DObject retorna apenas maxLength (cru: inches/65530).
      // PlotCalc espera maxLengthScaled = maxLength × 1e11 (protocolo VBA).
      // PlotCalcDataTransformer.cs faz esse scaling no fluxo C#-puro;
      // aqui precisamos replicar quando o campo escalado não vem do C#.
      maxLengthScaled: obj.maxLengthScaled ?? obj.MaxLengthScaled
        ?? ((obj.maxLength ?? obj.MaxLength ?? 0) * 100000000000),
      paths
    };
  });

  const shapeIds = curves.map((curve: NativeCurve) => curve.index);
  const sortedKeys = buildLegacySortedKeys(curves);
  const duplicateMap = buildLegacyDuplicateMap(curves, sortedKeys);

  sendToTerminal('[NATIVE-COLLECT] Payload.Resumo', {
    curves: curves.length,
    firstKey: sortedKeys[0] ?? null,
    sortedKeys0to10: sortedKeys.slice(0, 11),
    duplicateCount: Object.keys(duplicateMap).length,
    duplicateSample: Object.entries(duplicateMap)
      .slice(0, 10)
      .map(([duplicateKey, sourceKey]) => ({
        duplicateKey: Number(duplicateKey),
        sourceKey
      })),
    areas0to10: sortedKeys.slice(0, 11).map((key) => {
      const curve = curves.find((item: NativeCurve) => item.index === key);
      return curve ? Number((curve.width * curve.height).toFixed(3)) : null;
    })
  });

  return {
    version: 'native-csharp-1.0',
    elapsedMs,
    count: curves.length,
    shapeIds,
    sortedKeys,
    duplicateMap,
    curves
  };
}

function buildLegacySortedKeys(curves: NativeCurve[]): number[] {
  return curves
    .map((curve, position) => ({
      curve,
      position,
      area: getCurveArea(curve)
    }))
    .sort((first, second) => {
      const areaDelta = second.area - first.area;
      if (Math.abs(areaDelta) > 0.000001) {
        return areaDelta;
      }

      return first.position - second.position;
    })
    .map((item) => item.curve.index);
}

function buildLegacyDuplicateMap(
  curves: NativeCurve[],
  sortedKeys: number[]
): Record<string, number> {
  const curvesByKey = new Map(curves.map((curve) => [curve.index, curve]));
  const duplicateMap: Record<string, number> = {};

  for (let index = 1; index < sortedKeys.length; index += 1) {
    const currentKey = sortedKeys[index];
    const previousKey = sortedKeys[index - 1];
    const currentCurve = curvesByKey.get(currentKey);
    const previousCurve = curvesByKey.get(previousKey);

    if (currentCurve && previousCurve && areCurvesIdentical(currentCurve, previousCurve)) {
      duplicateMap[String(currentKey)] = previousKey;
    }
  }

  return duplicateMap;
}

function getCurveArea(curve: NativeCurve): number {
  const width = Number(curve.width);
  const height = Number(curve.height);

  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    return 0;
  }

  return Math.max(0, width) * Math.max(0, height);
}

function areCurvesIdentical(first: NativeCurve, second: NativeCurve): boolean {
  if (first.paths.length !== second.paths.length) {
    return false;
  }

  for (let pathIndex = 0; pathIndex < first.paths.length; pathIndex += 1) {
    const firstPath = first.paths[pathIndex];
    const secondPath = second.paths[pathIndex];

    if (firstPath.segments.length !== secondPath.segments.length) {
      return false;
    }

    for (let segmentIndex = 0; segmentIndex < firstPath.segments.length; segmentIndex += 1) {
      const firstSegment = firstPath.segments[segmentIndex];
      const secondSegment = secondPath.segments[segmentIndex];

      if (
        toLegacyInt(firstSegment.SNPx) !== toLegacyInt(secondSegment.SNPx) ||
        toLegacyInt(firstSegment.SNPy) !== toLegacyInt(secondSegment.SNPy) ||
        toLegacyInt(firstSegment.SCPx) !== toLegacyInt(secondSegment.SCPx) ||
        toLegacyInt(firstSegment.SCPy) !== toLegacyInt(secondSegment.SCPy) ||
        toLegacyInt(firstSegment.ECPx) !== toLegacyInt(secondSegment.ECPx) ||
        toLegacyInt(firstSegment.ECPy) !== toLegacyInt(secondSegment.ECPy)
      ) {
        return false;
      }
    }
  }

  return true;
}

function toLegacyInt(value: number): number {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? Math.floor(numericValue) : 0;
}
